import React, { useEffect, useRef, useState, useContext } from 'react';
import Peer from 'simple-peer';
import SocketContext from '../../context/SocketContext';
import { Button, Container, Row, Col } from 'react-bootstrap';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhoneSlash } from 'react-icons/fa';

const VideoCallRoom = ({ 
    callerMode = false, // True if we are dialing (Agency), False if receiving (Candidate)
    targetUserId, // Required if callerMode is true
    agencyPlanType,
    onEndCall 
}) => {
    const { socket, incomingCall, clearIncomingCall } = useContext(SocketContext);
    
    // Video elements
    const myVideoRef = useRef();
    const userVideoRef = useRef();
    const connectionRef = useRef();

    // Stream and states
    const [stream, setStream] = useState(null);
    const [callAccepted, setCallAccepted] = useState(false);
    const [callEnded, setCallEnded] = useState(false);
    
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    
    // Helper: create a fake video track from a canvas (for local testing when camera is locked)
    const createFakeVideoStream = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 480;
        const ctx = canvas.getContext('2d');
        
        // Draw a placeholder frame
        const drawFrame = () => {
            ctx.fillStyle = '#2d2d2d';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#ffffff';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('📷 Camera in use by other tab', canvas.width / 2, canvas.height / 2 - 15);
            ctx.font = '16px Arial';
            ctx.fillStyle = '#aaaaaa';
            ctx.fillText('Audio is still active', canvas.width / 2, canvas.height / 2 + 20);
        };
        drawFrame();
        const interval = setInterval(drawFrame, 1000);
        
        const fakeStream = canvas.captureStream(15); // 15 fps
        // Store interval ID so we can clean it up later
        fakeStream._canvasInterval = interval;
        return fakeStream;
    };

    // Initial fetch of user's camera / mic with graceful fallback
    useEffect(() => {
        const getMedia = async () => {
            try {
                // Try video + audio first
                const currentStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                setStream(currentStream);
                if (myVideoRef.current) {
                    myVideoRef.current.srcObject = currentStream;
                }
            } catch (err) {
                console.warn("Could not get video+audio, trying fake video + real audio:", err.message);
                try {
                    // Fallback: get audio only + fake canvas video
                    const audioStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
                    const fakeVideoStream = createFakeVideoStream();
                    
                    // Combine fake video track + real audio track
                    const combinedStream = new MediaStream([
                        ...fakeVideoStream.getVideoTracks(),
                        ...audioStream.getAudioTracks()
                    ]);
                    
                    setStream(combinedStream);
                    if (myVideoRef.current) {
                        myVideoRef.current.srcObject = combinedStream;
                    }
                } catch (audioErr) {
                    console.error("Could not get any media devices:", audioErr);
                    import('sweetalert2').then(Swal => {
                        Swal.default.fire(
                            'Camera/Mic Unavailable',
                            'Your camera or microphone could not be accessed. Please check permissions and try again.',
                            'warning'
                        );
                    });
                }
            }
        };
        getMedia();
            
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
                if (stream._canvasInterval) clearInterval(stream._canvasInterval);
            }
        };
    }, []);

    // Effect for handling the call setup automatically if stream is ready
    useEffect(() => {
        if (!stream || !socket) return;
        
        if (callerMode) {
            // We are the Agency placing a call
            callUser(targetUserId);
        } else if (incomingCall) {
            // We are the Candidate who just clicked "Accept"
            answerCall(incomingCall);
        }
    }, [stream, socket, callerMode, incomingCall, targetUserId]);

    // Use a ref to prevent answering the same call multiple times (which causes setRemoteDescription errors)
    const hasAnsweredRef = useRef(false);

    // Agency initiates peer connection
    const callUser = (idToCall) => {
        // Create an initiator peer
        const peer = new Peer({
            initiator: true,
            trickle: false,
            stream: stream
        });

        // Whenever SimplePeer generates an offer, forward it via socket
        peer.on('signal', (data) => {
             // Retrieve from localStorage or state later
             const callerId = localStorage.getItem('userId'); 
             const callerName = localStorage.getItem('company_name') || 'Agency';
             
             socket.emit('call-user', {
                 targetUserId: idToCall,
                 callerId: callerId,
                 callerName: callerName,
                 signalData: data,
                 agencyPlanType: agencyPlanType
             });
        });

        // When remote stream arrives, display it
        peer.on('stream', (currentStream) => {
            if (userVideoRef.current) {
                userVideoRef.current.srcObject = currentStream;
            }
        });

        // Catch WebRTC connection errors so they don't crash the app
        peer.on('error', (err) => {
            console.error('Peer connection error:', err);
            setCallEnded(true);
            if (connectionRef.current) connectionRef.current.destroy();
            cleanupListeners && cleanupListeners();
        });

        // Wait to receive 'call-answered' back from candidate
        const handleCallAnswered = (signalData) => {
            if (!callAccepted) {
                setCallAccepted(true);
                peer.signal(signalData.signalData);
            }
        };
        socket.on('call-answered', handleCallAnswered);
        
        // Handle target closing call
        const handleCallEnded = () => {
            setCallEnded(true);
            if (connectionRef.current) connectionRef.current.destroy();
            cleanupListeners();
        };
        socket.on('call-ended', handleCallEnded);

        // Handle target offline
        const handleCallFailed = (data) => {
            console.log('Call failed:', data.reason);
            setCallEnded(true);
            if (connectionRef.current) connectionRef.current.destroy();
            cleanupListeners();
            import('sweetalert2').then(Swal => {
                 Swal.default.fire('Candidate Offline', 'The candidate is currently not online. Please try again later.', 'info').then(() => {
                     if (onEndCall) onEndCall();
                 });
            });
        };
        socket.on('call-failed', handleCallFailed);
        
        const cleanupListeners = () => {
             socket.off('call-answered', handleCallAnswered);
             socket.off('call-ended', handleCallEnded);
             socket.off('call-failed', handleCallFailed);
        };

        connectionRef.current = peer;
    };

    // Candidate answers peer connection
    const answerCall = (data) => {
        // Prevent answering multiple times (prevents "Called in wrong state: stable" error)
        if (hasAnsweredRef.current) return;
        hasAnsweredRef.current = true;
        
        setCallAccepted(true);

        const peer = new Peer({
            initiator: false,
            trickle: false,
            stream: stream
        });

        // Send 'answer' signal back
        peer.on('signal', (signalData) => {
            socket.emit('answer-call', { 
                responderId: localStorage.getItem('userId'),
                callerId: data.callerId,
                signalData: signalData 
            });
        });

        // When agency stream arrives, display it
        peer.on('stream', (currentStream) => {
            if (userVideoRef.current) {
                userVideoRef.current.srcObject = currentStream;
            }
        });

        // Catch WebRTC connection errors so they don't crash the app
        peer.on('error', (err) => {
            console.error('Peer connection error:', err);
            setCallEnded(true);
            if (connectionRef.current) connectionRef.current.destroy();
        });

        peer.signal(data.signalData);
        connectionRef.current = peer;
        
        // Handle target closing call
        const handleCallEnded = () => {
            setCallEnded(true);
            if (connectionRef.current) connectionRef.current.destroy();
            socket.off('call-ended', handleCallEnded);
        };
        socket.on('call-ended', handleCallEnded);
        
        // Also cleanup on component unmount
        return () => {
            socket.off('call-ended', handleCallEnded);
        };
    };

    const leaveCall = () => {
        setCallEnded(true);
        if (connectionRef.current) {
            connectionRef.current.destroy();
        }
        
        socket.emit('end-call', {
            // we don't know who we are exactly relative to them here
            // but we broadcast to the counterparty id if we stored it
            toUserId: incomingCall ? incomingCall.callerId : targetUserId
        });

        clearIncomingCall();
        if (onEndCall) onEndCall();
    };

    const toggleMic = () => {
        if (stream) {
            const audioTracks = stream.getAudioTracks();
            let newMutedState = !isMuted;
            
            audioTracks.forEach(track => {
                track.enabled = !newMutedState;
            });
            
            // For simple-peer/WebRTC, sometimes just disabling the track isn't enough,
            // we should also try to stop the sender if possible
            if (connectionRef.current && connectionRef.current._pc) {
                const senders = connectionRef.current._pc.getSenders();
                const audioSender = senders.find(s => s.track && s.track.kind === 'audio');
                if (audioSender && audioSender.track) {
                    audioSender.track.enabled = !newMutedState;
                }
            }
            
            setIsMuted(newMutedState);
        }
    };

    const toggleVideo = () => {
        if (stream) {
            const videoTracks = stream.getVideoTracks();
            let newVideoOffState = !isVideoOff;
            
            videoTracks.forEach(track => {
                track.enabled = !newVideoOffState;
            });
            
            if (connectionRef.current && connectionRef.current._pc) {
                const senders = connectionRef.current._pc.getSenders();
                const videoSender = senders.find(s => s.track && s.track.kind === 'video');
                if (videoSender && videoSender.track) {
                    videoSender.track.enabled = !newVideoOffState;
                }
            }
            
            setIsVideoOff(newVideoOffState);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: '#1E1E1E',
            zIndex: 99999, // Overlay the whole app
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
           <Container fluid className="h-100 p-0 position-relative">
                {/* Remote Video (Full Screen) */}
                {callAccepted && !callEnded ? (
                    <video 
                        playsInline 
                        ref={userVideoRef} 
                        autoPlay 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                ) : (
                    <div className="d-flex w-100 h-100 justify-content-center align-items-center flex-column text-white">
                        <div className="spinner-grow text-success mb-3" role="status" style={{width: '4rem', height: '4rem'}}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h3>{callerMode ? `Calling...` : `Connecting...`}</h3>
                        {callEnded && <h5 className="text-danger mt-3">Call Ended</h5>}
                    </div>
                )}

                {/* Local Video (Floating bottom right) */}
                {stream && (
                    <div style={{
                        position: 'absolute',
                        bottom: '90px',
                        right: '25px',
                        width: '200px',
                        height: '150px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
                        border: '3px solid #333'
                    }}>
                        <video 
                            playsInline 
                            muted 
                            ref={myVideoRef} 
                            autoPlay 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                    </div>
                )}

                {/* Control Bar (Bottom Center) */}
                <div style={{
                    position: 'absolute',
                    bottom: '25px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    padding: '12px 24px',
                    borderRadius: '40px',
                    display: 'flex',
                    gap: '15px'
                }}>
                    <Button 
                        variant={isMuted ? "danger" : "light"} 
                        className="rounded-circle d-flex justify-content-center align-items-center"
                        style={{ width: '50px', height: '50px' }}
                        onClick={toggleMic}
                    >
                        {isMuted ? <FaMicrophoneSlash size={20} /> : <FaMicrophone size={20} />}
                    </Button>
                    
                    <Button 
                        variant="danger" 
                        className="rounded-circle d-flex justify-content-center align-items-center"
                        style={{ width: '65px', height: '65px' }}
                        onClick={leaveCall}
                    >
                        <FaPhoneSlash size={26} />
                    </Button>

                    <Button 
                        variant={isVideoOff ? "danger" : "light"} 
                        className="rounded-circle d-flex justify-content-center align-items-center"
                        style={{ width: '50px', height: '50px' }}
                        onClick={toggleVideo}
                    >
                        {isVideoOff ? <FaVideoSlash size={20} /> : <FaVideo size={20} />}
                    </Button>
                </div>
           </Container>
        </div>
    );
};

export default VideoCallRoom;
